import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import SalonContextSwitcher from '../../components/ui/SalonContextSwitcher';
import EmployeeTable from './components/EmployeeTable';
import EmployeeModal from './components/EmployeeModal';
import EmployeeFilters from './components/EmployeeFilters';
import BulkActionsBar from './components/BulkActionsBar';
import AvailabilityModal from './components/AvailabilityModal';
import api from 'utils/api';

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedSalon, setSelectedSalon] = useState(
    '68c806bf374b8c596edb208c',
  ); // real salonId
  const [services, setServices] = useState([]);

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [availabilityEmployee, setAvailabilityEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ status: '', services: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    role: '',
    services: [],
  });

  const getServices = async () => {
    try {
      const response = await api.get(
        `/salons/651a6b2f8b7a5a1d223e4c90/services`,
      );
      console.log(response.data);
      setServices(response.data);
      return response.data; // array of services
    } catch (error) {
      console.error('Error fetching services:', error);
      return []; // return empty array on error
    }
  };

  useEffect(() => {
    getServices();
    // Initialize services data
    // setServices(mockServices);
    // setFilteredServices(mockServices);
  }, [selectedSalon]);

  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setIsEmployeeModalOpen(true);
  };

  // Load employees from backend
  useEffect(() => {
    console.log('start getting employees');
    const fetchEmployees = async () => {
      try {
        setIsLoading(true);
        const { data } = await api.get(`/salons/${selectedSalon}/employees`);
        setEmployees(data);
        console.log('got employees', data);
        setFilteredEmployees(data);
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (selectedSalon) fetchEmployees();
  }, [selectedSalon]);

  // Apply search & filters locally
  useEffect(() => {
    let filtered = [...employees];

    if (searchTerm) {
      filtered = filtered.filter(
        (employee) =>
          employee?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee?.phone?.includes(searchTerm) ||
          employee?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee?.employeeCode
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    if (filters?.status) {
      filtered = filtered.filter(
        (employee) => employee?.status === filters.status,
      );
    }

    if (filters?.services?.length > 0) {
      filtered = filtered.filter((employee) =>
        filters.services.some((serviceId) =>
          employee?.serviceIds?.includes(parseInt(serviceId)),
        ),
      );
    }

    setFilteredEmployees(filtered);
  }, [employees, searchTerm, filters]);

  // Save (add or edit) employee
  const handleSaveEmployee = async (employeeData) => {
    console.log('start storing employees');
    try {
      if (editingEmployee) {
        // Update existing
        const { data } = await api.put(
          `/salons/${selectedSalon}/employees/${editingEmployee.id}`,
          employeeData,
        );
        console.log('stored employees', data);
        setEmployees((prev) =>
          prev.map((emp) => (emp.id === editingEmployee.id ? data : emp)),
        );
      } else {
        // Add new
        const { data } = await api.post(
          `/salons/${selectedSalon}/employees`,
          employeeData,
        );
        setEmployees((prev) => [...prev, data]);
      }
    } catch (error) {
      console.error('Error saving employee:', error);
    }
  };

  // Delete employee
  const handleDeleteEmployee = async (employee) => {
    if (
      window.confirm(
        `آیا مطمئن هستید که می‌خواهید ${employee?.name} را حذف کنید؟`,
      )
    ) {
      try {
        await api.delete(`/salons/${selectedSalon}/employees/${employee.id}`);
        setEmployees((prev) => prev.filter((emp) => emp.id !== employee.id));
        setSelectedEmployees((prev) => prev.filter((id) => id !== employee.id));
      } catch (error) {
        console.error('Error deleting employee:', error);
      }
    }
  };

  const handleSelectEmployee = (employeeId, selected) => {
    setSelectedEmployees((prev) =>
      selected ? [...prev, employeeId] : prev.filter((id) => id !== employeeId),
    );
  };

  const handleSelectAll = (selected) => {
    setSelectedEmployees(
      selected ? filteredEmployees.map((employee) => employee.id) : [],
    );
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setIsEmployeeModalOpen(true);
  };

  const handleManageAvailability = (employee) => {
    setAvailabilityEmployee(employee);
    setIsAvailabilityModalOpen(true);
  };

  const handleViewSchedule = (employee) => {
    navigate(`/availability-calendar?employee=${employee.id}`);
  };

  // Bulk actions example
  const handleBulkStatusChange = async (status) => {
    try {
      await api.put(`/salons/${selectedSalon}/employees/bulk-status`, {
        employeeIds: selectedEmployees,
        status,
      });
      setEmployees((prev) =>
        prev.map((emp) =>
          selectedEmployees.includes(emp.id) ? { ...emp, status } : emp,
        ),
      );
      setSelectedEmployees([]);
    } catch (error) {
      console.error('Error updating bulk status:', error);
    }
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleBulkDelete = async () => {
    try {
      await api.post(`/salons/${selectedSalon}/employees/bulk-delete`, {
        employeeIds: selectedEmployees,
      });
      setEmployees((prev) =>
        prev.filter((emp) => !selectedEmployees.includes(emp.id)),
      );
      setSelectedEmployees([]);
    } catch (error) {
      console.error('Error bulk deleting employees:', error);
    }
  };

  // Manage availability
  const handleSaveAvailability = async (employeeId, availability) => {
    try {
      const { data } = await api.put(
        `/salons/${selectedSalon}/employees/${employeeId}/availability`,
        availability,
      );
      setEmployees((prev) =>
        prev.map((emp) => (emp.id === employeeId ? { ...emp, ...data } : emp)),
      );
    } catch (error) {
      console.error('Error saving availability:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        {/* <Header
          isCollapsed={!isSidebarOpen}
          onToggleSidebar={handleSidebarToggle}
        /> */}
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <main className="lg:mr-60 pt-16">
          <div className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon
                  name="Loader2"
                  size={32}
                  className="text-primary animate-spin mx-auto mb-4"
                />
                <p className="text-muted-foreground">
                  در حال بارگذاری اطلاعات کارمندان...
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      {/*
      <Header
        isCollapsed={!isSidebarOpen}
        onToggleSidebar={handleSidebarToggle}
      /> */}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <main className="lg:mr-60 pt-16">
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="space-y-4">
            <BreadcrumbNavigation />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-foreground flex items-center gap-3">
                  <Icon name="Users" size={28} className="text-primary" />
                  مدیریت کارمندان
                </h1>
                <p className="text-muted-foreground mt-1">
                  مدیریت کارمندان، خدمات و برنامه‌های کاری سالن
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/availability-calendar')}
                  iconName="Calendar"
                  iconPosition="left">
                  تقویم دسترسی
                </Button>
                <Button
                  onClick={handleAddEmployee}
                  iconName="Plus"
                  iconPosition="left">
                  افزودن کارمند
                </Button>
              </div>
            </div>
          </div>

          {/* Salon Context Switcher */}
          <SalonContextSwitcher
            selectedSalon={selectedSalon}
            //onSalonChange={handleSalonChange}
          />

          {/* Filters */}
          {/* <EmployeeFilters
            // onSearch={handleSearch}
            //  onFilterChange={handleFilterChange}
            //
            // availableServices={mockServices}
            totalEmployees={employees?.length}
            filteredCount={filteredEmployees?.length}
          /> */}

          {/* Bulk Actions */}
          <BulkActionsBar
            selectedCount={selectedEmployees?.length}
            onBulkStatusChange={handleBulkStatusChange}
            onBulkDelete={handleBulkDelete}
            // onBulkServiceAssign={handleBulkServiceAssign}
            //   onClearSelection={handleClearSelection}
            // availableServices={mockServices}
          />

          {/* Employee Table */}
          <EmployeeTable
            employees={filteredEmployees}
            selectedEmployees={selectedEmployees}
            onSelectEmployee={handleSelectEmployee}
            onSelectAll={handleSelectAll}
            onEditEmployee={handleEditEmployee}
            onManageAvailability={handleManageAvailability}
            onViewSchedule={handleViewSchedule}
            onDeleteEmployee={handleDeleteEmployee}
          />

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="UserCheck" size={20} className="text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">در دسترس</p>
                  <p className="text-lg font-semibold text-foreground">
                    {
                      employees?.filter((emp) => emp?.status === 'available')
                        ?.length
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">مشغول</p>
                  <p className="text-lg font-semibold text-foreground">
                    {employees?.filter((emp) => emp?.status === 'busy')?.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Scissors" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">کل خدمات</p>
                  <p className="text-lg font-semibold text-foreground">
                    {/* {mockServices?.length} */}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Star" size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    میانگین امتیاز
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    {(
                      employees?.reduce((acc, emp) => acc + emp?.rating, 0) /
                      employees?.length
                    )?.toFixed(1)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      <EmployeeModal
        isOpen={isEmployeeModalOpen}
        onClose={() => setIsEmployeeModalOpen(false)}
        employee={editingEmployee}
        onSave={handleSaveEmployee}
        availableServices={services}
        selectedSalon={selectedSalon}
      />
      <AvailabilityModal
        isOpen={isAvailabilityModalOpen}
        onClose={() => setIsAvailabilityModalOpen(false)}
        employee={availabilityEmployee}
        onSave={handleSaveAvailability}
      />
    </div>
  );
};

export default EmployeeManagement;
